import { ReactElement, useEffect, useLayoutEffect, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useMatch } from 'react-router-dom'
import { FloorplanImage, SceneErrorIndicatorFallback } from '@scene'
import { useGuidToDomain, useStoreyImages } from '@editor/hooks'
import { BasicMesh, useCameraStore } from '@modugen/ui-toolkit'
import {
  useControlStore,
  useEditorViewStore,
  useElementLoadStore,
  useResultsStore,
  useStructuralPlanningStore,
  useTapelineStore,
} from '@editorStores'
import useVisibleModel from 'src/components/pages/Editor/hooks/useVisibleModel'
import { useModelSnapTargets } from '../../hooks'
import { maxLineLoadMagnitude } from '../FloorplanLineLoadsMesh/utils'
import FloorplanLoadsMesh from '../FloorplanLoadsMesh'
import { maxPointLoadMagnitude } from '../FloorplanPointLoadsMesh/utils'

interface Props {
  activeStorey: string
}

const FloorPlan = ({ activeStorey }: Props): ReactElement => {
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)

  const cameraRotationTarget = useCameraStore(state => state.rotationTarget)
  const { storeyImages } = useStoreyImages()

  const setTapelineSnapTargets = useTapelineStore(state => state.setAdditionalSnapTargets)

  const visibleModel = useVisibleModel()

  const snapTargets = useModelSnapTargets({ xyOnly: true })

  // EFFECTS

  useLayoutEffect(() => {
    setIsDrawingActive(true)

    return () => {
      setIsDrawingActive(false)
    }
  }, [setIsDrawingActive])

  useEffect(() => {
    setTapelineSnapTargets(snapTargets)

    return setTapelineSnapTargets
  }, [setTapelineSnapTargets, snapTargets])

  const guidToDomain = useGuidToDomain()

  const isResultsView = !!useMatch({ path: '/editor/:projectId/results', end: false })

  // Loads and visibility state
  const loads = useElementLoadStore(state => state.loads)
  const visibility = useControlStore(state => state.structuralPlanningElementVisibility)
  const resultsVisibleLoadTypes = useEditorViewStore(state => state.results.visibleLoadTypes)
  const structuralPlanningHiddenLoads = useStructuralPlanningStore(state => state.hiddenLoads)
  const resultsHiddenLoads = useResultsStore(state => state.hiddenLoads)
  const hiddenLoads = isResultsView ? resultsHiddenLoads : structuralPlanningHiddenLoads
  const areaLoadsVisible = isResultsView
    ? visibility.areaLoads && (resultsVisibleLoadTypes['area-load'] ?? true)
    : visibility.areaLoads

  // Create set of all visible element GUIDs
  const visibleElementGuids = useMemo(() => {
    const allElements = [
      ...visibleModel.walls,
      ...visibleModel.slabs,
      ...visibleModel.roof_slabs,
      ...visibleModel.vertical_slabs,
      ...visibleModel.vertical_roof_slabs,
      ...visibleModel.beams,
      ...visibleModel.columns,
      ...visibleModel.purlins,
      ...visibleModel.foundationElements,
    ]

    // Also include beam elements from vertical slabs and vertical roof slabs
    const beamElements = [
      ...visibleModel.vertical_slabs.map(slab => slab.beam).filter(Boolean),
      ...visibleModel.vertical_roof_slabs.map(slab => slab.beam).filter(Boolean),
    ]

    return new Set([
      ...allElements.map(element => element.guid),
      ...beamElements.map(beam => beam.guid),
    ])
  }, [visibleModel])

  // Filter area loads based on visibility, hidden loads, and element visibility
  const filteredAreaLoads = useMemo(() => {
    return loads.filter(load => {
      if (load.load_type !== 'area-load') return false
      if (hiddenLoads.includes(load.guid)) return false
      if (!areaLoadsVisible) return false
      if (!visibleElementGuids.has(load.element_guid)) return false
      return true
    }) as AreaLoad[]
  }, [loads, hiddenLoads, areaLoadsVisible, visibleElementGuids])

  // Filter point loads and enhance with domain information
  const filteredPointLoads = useMemo(() => {
    const pointLoads = loads.filter(load => {
      if (load.load_type !== 'point-load') return false
      if (hiddenLoads.includes(load.guid)) return false
      if (!visibility.pointLoads) return false
      if (!visibleElementGuids.has(load.element_guid)) return false
      return true
    }) as PointLoad[]

    // Enhance point loads with domain information
    return pointLoads
      .map(load => {
        const domain = guidToDomain[load.domain_guid]
        if (!domain) return null
        return {
          ...load,
          domain,
        } as PointLoadWithDomain
      })
      .filter(Boolean) as PointLoadWithDomain[]
  }, [loads, hiddenLoads, visibility.pointLoads, visibleElementGuids, guidToDomain])

  const lineLoadsVisible = isResultsView
    ? visibility.lineLoads && (resultsVisibleLoadTypes['line-load'] ?? true)
    : visibility.lineLoads

  const filteredLineLoads = useMemo(() => {
    if (!lineLoadsVisible) return []

    return loads.flatMap(load => {
      if (load.load_type !== 'line-load') return []
      if (hiddenLoads.includes(load.guid)) return []
      if (!visibleElementGuids.has(load.element_guid)) return []

      const domain = guidToDomain[load.domain_guid]
      if (!domain) return []

      return [{ ...load, domain } as LineLoadWithDomain]
    })
  }, [loads, hiddenLoads, lineLoadsVisible, visibleElementGuids, guidToDomain])

  // over all line loads of the project, so hiding loads or switching storeys keeps the widths
  const maxLineLoadValue = useMemo(
    () =>
      maxLineLoadMagnitude(
        loads.filter((load): load is LineLoad => load.load_type === 'line-load'),
      ),
    [loads],
  )

  const maxPointLoadValue = useMemo(
    () =>
      maxPointLoadMagnitude(
        loads.filter((load): load is PointLoad => load.load_type === 'point-load'),
      ),
    [loads],
  )

  const visibleLoads = useMemo(
    () => [...filteredAreaLoads, ...filteredPointLoads, ...filteredLineLoads],
    [filteredAreaLoads, filteredPointLoads, filteredLineLoads],
  )

  return (
    <>
      <FloorplanLoadsMesh
        loads={visibleLoads}
        maxLineLoadMagnitude={maxLineLoadValue}
        maxPointLoadMagnitude={maxPointLoadValue}
      />

      {/* invisible plane serves as a hit target for the draw controller */}
      <BasicMesh position={[cameraRotationTarget.x, cameraRotationTarget.y, -0.1]}>
        <planeGeometry args={[100, 100]} />
        <meshStandardMaterial transparent opacity={0} />
      </BasicMesh>

      {activeStorey && storeyImages[activeStorey] && storeyImages[activeStorey]?.isVisible2D && (
        <ErrorBoundary
          fallbackRender={({ error }) => (
            <SceneErrorIndicatorFallback
              error={error}
              context={'Fehler beim Laden der Hintergrundfolie. '}
            />
          )}
        >
          <FloorplanImage
            key={`storey-image-${activeStorey}`}
            scale={storeyImages[activeStorey]!.scale}
            offsetX={storeyImages[activeStorey]!.offsetX}
            offsetY={storeyImages[activeStorey]!.offsetY}
            rotation={storeyImages[activeStorey]!.rotation}
            foregroundTransparency={storeyImages[activeStorey]!.transparencyConfig['2D'].foreground}
            backgroundTransparency={storeyImages[activeStorey]!.transparencyConfig['2D'].background}
            paperFormat={storeyImages[activeStorey]!.paperFormat}
            paperWidthCm={storeyImages[activeStorey]!.paperWidthCm}
            url={storeyImages[activeStorey]!.url}
          />
        </ErrorBoundary>
      )}
    </>
  )
}

export default FloorPlan
